/* Customer-facing token board — public URL /display.

Fetches /api/tokens/display every 8 s (no auth required). Renders as a grid
of tokens with the station name(s) responsible. When a new token appears vs.
the previous poll, briefly flashes that card. If there are more tokens than
fit in a single visible page, cycles through pages every 8 s. */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/pos";

const REFRESH_MS = 8000;
const PAGE_ROTATE_MS = 8000;
const PAGE_SIZE = 12; // 3 rows × 4 columns per page — pagination cycles if there are more.

export default function TokenDisplay() {
  const [tokens, setTokens] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [flashIds, setFlashIds] = useState(new Set());
  const prevRef = useRef(new Set());
  const [now, setNow] = useState(Date.now());

  // Fire-and-forget QR scan tracking — runs once when someone opens /display?ref=qr.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("ref") === "qr") {
        const key = "qoh_qr_tracked_session";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          axios.post(`${API}/qr/track?ref=qr`, {}, { withCredentials: false }).catch(() => {});
        }
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Fetch loop — noAuth via a plain axios (no cookies needed, endpoint is public).
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/tokens/display`, { withCredentials: false });
        if (stopped) return;
        const next = Array.isArray(r.data) ? r.data : [];
        const prev = prevRef.current;
        // Unique per (order × station) since a single order can flash multiple times as each
        // station marks its portion ready.
        const keyOf = (t) => `${t.order_id}::${t.station_id || ""}`;
        const nextKeys = new Set(next.map(keyOf));
        const fresh = new Set([...nextKeys].filter((id) => !prev.has(id)));
        prevRef.current = nextKeys;
        setTokens(next);
        if (fresh.size) {
          setFlashIds(fresh);
          setTimeout(() => setFlashIds(new Set()), 3000);
        }
      } catch (_) { /* keep last state on transient error */ }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { stopped = true; clearInterval(t); clearInterval(clock); };
  }, []);

  const pages = useMemo(() => {
    if (!tokens.length) return [[]];
    const out = [];
    for (let i = 0; i < tokens.length; i += PAGE_SIZE) out.push(tokens.slice(i, i + PAGE_SIZE));
    return out;
  }, [tokens]);

  useEffect(() => {
    // Keep pageIdx in bounds when tokens shrink.
    if (pageIdx >= pages.length) setPageIdx(0);
  }, [pages.length, pageIdx]);

  useEffect(() => {
    if (pages.length <= 1) return; // no rotation needed
    const t = setInterval(() => setPageIdx((i) => (i + 1) % pages.length), PAGE_ROTATE_MS);
    return () => clearInterval(t);
  }, [pages.length]);

  const visible = pages[pageIdx] || [];

  return (
    <div className="token-display-root" data-testid="token-display-root">
      <style>{`
        html,body,#root{height:100%;margin:0}
        .token-display-root{position:fixed;inset:0;background:#050f08;color:#e9ffe0;padding:20px 28px 24px;box-sizing:border-box;font-family:'Times New Roman','Liberation Serif',Times,serif;display:flex;flex-direction:column;overflow:hidden}
        .td-heading{font-size:min(6vh, 5.2vw);letter-spacing:5px;text-transform:uppercase;color:#8dd97a;margin:0 0 18px;text-align:center;flex:0 0 auto;font-weight:800}
        .td-empty{flex:1;display:flex;align-items:center;justify-content:center;font-size:44px;color:#3d6a35;letter-spacing:2px;text-transform:uppercase}
        /* 4-column grid with content-sized rows — each card grows to fit its contents (dynamic height). */
        .td-grid{flex:1;display:grid;grid-template-columns:repeat(4, 1fr);grid-auto-rows:minmax(min-content, 1fr);gap:14px;padding:2px 0;min-height:0;align-content:start}
        @media (max-width: 900px){.td-grid{grid-template-columns:repeat(3, 1fr);gap:12px}}
        @media (max-width: 720px){.td-grid{grid-template-columns:repeat(2, 1fr);gap:10px}.token-display-root{padding:14px 14px 18px}.td-card{padding:18px 10px}.td-card .num{font-size:min(11vh, 22vw)}.td-card .stn{font-size:min(3.4vh, 5.4vw);letter-spacing:1.5px}}
        @media (max-width: 420px){.td-grid{grid-template-columns:1fr;gap:8px}.td-card{padding:22px 12px}.td-card .num{font-size:22vw}.td-card .stn{font-size:5vw}}
        .td-card{background:linear-gradient(180deg, #0f3d1a, #072812);border:2px solid #21b04a;border-radius:14px;padding:14px 12px;text-align:center;box-shadow:0 6px 22px rgba(0,0,0,0.55), inset 0 0 22px rgba(35,180,80,0.15);position:relative;overflow:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:0;min-width:0;box-sizing:border-box;transition:background .3s, border-color .3s}
        .td-card .num{font-size:min(14vh, 15vw);font-weight:800;color:#ecffe4;line-height:1;letter-spacing:2px;font-family:'Times New Roman',serif;text-shadow:0 0 24px rgba(90, 240, 120, 0.55)}
        .td-card .stn{margin-top:10px;font-size:min(4.6vh, 4vw);font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8f7c0;line-height:1.15;min-height:1.15em;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;max-width:100%}
        .td-flash{animation:td-flash 1.2s ease-in-out infinite;border-color:#7dff9c;box-shadow:0 0 32px rgba(120, 255, 140, 0.85), inset 0 0 22px rgba(35,180,80,0.35)}
        @keyframes td-flash{0%,100%{transform:scale(1);box-shadow:0 0 24px rgba(120, 255, 140, 0.55)}50%{transform:scale(1.04);box-shadow:0 0 48px rgba(120, 255, 140, 1)}}
        /* Stale (waiting >5 min) — orange, blinking, to nudge the guest to please pick up */
        .td-stale{background:linear-gradient(180deg,#4a2708,#2b1502);border-color:#ffa64a;animation:td-blink 1.1s ease-in-out infinite;box-shadow:0 0 28px rgba(255,166,74,0.7), inset 0 0 22px rgba(255,140,40,0.25)}
        .td-stale .num{color:#ffedcb;text-shadow:0 0 24px rgba(255,180,90,0.85)}
        .td-stale .stn{color:#ffd9a8}
        @keyframes td-blink{0%,100%{opacity:1;box-shadow:0 0 28px rgba(255,166,74,0.7), inset 0 0 22px rgba(255,140,40,0.25)}50%{opacity:.55;box-shadow:0 0 44px rgba(255,166,74,1), inset 0 0 32px rgba(255,140,40,0.45)}}
        .td-page-indicator{text-align:center;font-size:11px;color:#5aa447;letter-spacing:3px;text-transform:uppercase;padding-top:6px;flex:0 0 auto}
        .td-footer{flex:0 0 auto;text-align:center;padding:10px 12px 6px;line-height:1.55;font-family:'Times New Roman',serif}
        .td-footer a{display:block;color:#c8f7c0;font-weight:600;font-size:16px;letter-spacing:1.5px;text-decoration:none;margin-bottom:4px}
        .td-footer a:hover{text-decoration:underline}
        .td-footer small{display:block;font-size:11px;color:#5aa447;letter-spacing:2px;text-transform:uppercase}
      `}</style>
      <h1 className="td-heading">Ready for pickup</h1>
      {visible.length === 0 ? (
        <div className="td-empty">— No orders ready —</div>
      ) : (
        <div className="td-grid" data-testid="td-grid">
          {visible.map((t) => {
            const key = `${t.order_id}::${t.station_id || ""}`;
            const readyMs = t.ready_at ? Date.parse(t.ready_at) : now;
            const ageSec = Math.max(0, (now - readyMs) / 1000);
            const isStale = ageSec >= 300; // 5 minutes
            const cls = ["td-card"];
            if (flashIds.has(key)) cls.push("td-flash");
            if (isStale) cls.push("td-stale");
            return (
              <div key={key} data-testid={`td-token-${t.token}-${t.station_id || "kitchen"}`}
                className={cls.join(" ")}>
                <div className="num">T{String(t.token).padStart(2, "0")}</div>
                <div className="stn">{t.station_name || (t.stations || []).join(" · ") || "Kitchen"}</div>
              </div>
            );
          })}
        </div>
      )}
      {pages.length > 1 && (
        <div className="td-page-indicator">Page {pageIdx + 1} of {pages.length} · rotates every 8s</div>
      )}
      <footer className="td-footer" data-testid="td-footer">
        <a
          href="https://shimlafoodcourt.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="td-footer-link"
        >Shimla QoH Food Court, Near Lift, Shimla</a>
        <small>Copyright {new Date().getFullYear()} · KKK Food Court Pvt Ltd</small>
      </footer>
    </div>
  );
}
